<script setup lang="ts">
import { computed, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import BaseButton from '@/components/BaseButton.vue';
import { useDemoAccount } from '@/composables/useDemoAccount';
import BaseModal from '@/components/BaseModal.vue';
import BrandIcon from '@/components/brand/BrandIcon.vue';
import { useAuth } from '@/composables/firebase/auth';
import AppAvatar from '@/components/avatar/AppAvatar.vue';

const {
  canCreateDemoAccount,
  createDemoAccount,
  demoAccount,
} = useDemoAccount();

const { user } = useAuth();
const { t } = useI18n();
const toast = useToast();

const isDemoAccountModalVisible = ref(false);

const steps = ref([...Array(4).keys()]
  .map((i) => ({
    message: t(`customer.demoAccount.createModal.steps.${i}.message`),
    description: t(`customer.demoAccount.createModal.steps.${i}.description`),
    status: i === 0 ? 'inProgress' : 'waiting',
  })));

const stepsFinished = computed(() => steps.value.filter((step) => step.status === 'finished').length === steps.value.length);
const isReady = computed(() => !!demoAccount.value && stepsFinished.value);

async function startDemoAccountCreationFlow() {
  isDemoAccountModalVisible.value = true;

  let activeStepIndex = 0;
  const messagesInterval = setInterval(() => {
    steps.value[activeStepIndex].status = 'finished';

    if ((activeStepIndex + 1) === steps.value.length) {
      clearInterval(messagesInterval);
      return;
    }

    activeStepIndex += 1;
    steps.value[activeStepIndex].status = 'inProgress';
  }, 1500);

  createDemoAccount().catch(() => {
    clearInterval(messagesInterval);
    activeStepIndex = 0;
    isDemoAccountModalVisible.value = false;
    toast.error(t('generic.errors.generic'));
  });
}
</script>

<template>
  <div>
    <slot
      v-if="canCreateDemoAccount"
      name="action"
      :create-demo-account="startDemoAccountCreationFlow">
      <BaseButton
        class="btn btn-primary"
        icon="rocket"
        @click="startDemoAccountCreationFlow">
        {{ $t('customer.demoAccount.createCta') }}
      </BaseButton>
    </slot>

    <BaseModal
      v-if="isDemoAccountModalVisible"
      :can-close="false"
      :show-close-action="false"
      class="demo-account-modal"
      translucent-header
      blur-background>
      <template #default>
        <div class="demo-account-modal-content">
          <BrandIcon />

          <ul>
            <li
              v-for="(step, index) in steps"
              :key="`step-${index}`"
              class="step"
              :class="[`step--${step.status}`]">
              <font-awesome-icon
                v-if="step.status === 'inProgress'"
                size="xl"
                icon="circle-notch"
                spin />

              <font-awesome-icon
                v-if="step.status === 'waiting'"
                size="xl"
                :icon="['far', 'circle']" />

              <font-awesome-icon
                v-if="step.status === 'finished'"
                size="xl"
                :icon="['far', 'circle-check']" />

              <div>
                <div class="step-message">
                  {{ step.message }}
                </div>

                <div
                  v-if="step.description"
                  class="step-description">
                  {{ step.description }}
                </div>
              </div>
            </li>
          </ul>

          <div
            v-if="isReady && user && demoAccount?.profile"
            class="summary">
            <div class="text-bold text-l text">
              {{ $t('customer.demoAccount.createModal.summary.headline') }}
            </div>
            <div class="text-m">
              {{ $t('customer.demoAccount.createModal.summary.description') }}
            </div>

            <div class="mt-5">
              <RouterLink
                :to="{
                  name: 'customerDashboard',
                  params: {
                    customerId: user.authUser.uid,
                  },
                }">
                <div class="flex items-center">
                  <AppAvatar
                    class="avatar"
                    :customer="demoAccount"
                    :size="80" />

                  <div class="ml-3 text-left">
                    <div class="mb-1 text-bold">
                      {{ `${demoAccount.profile.personalData?.firstName} ${demoAccount.profile.personalData?.surname}` }}
                    </div>

                    <div class="text-m">
                      {{ demoAccount.profile.personalData?.email }}
                    </div>

                    <div class="mt-2">
                      <RouterLink
                        :to="{
                          name: 'customerDashboard',
                          params: {
                            customerId: user.authUser.uid,
                          },
                        }"
                        class="btn btn--small btn-primary">
                        <font-awesome-icon icon="user" class="mr-2" />
                        {{ $t('customer.demoAccount.createModal.summary.profileCta') }}
                      </RouterLink>
                    </div>
                  </div>
                </div>
              </RouterLink>
            </div>
          </div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<style>
.demo-account-modal {
  .modal-body {
    background: url('@/assets/img/kc-bg-light.jpg') no-repeat center center / cover;
    padding: var(--space-5);
  }

  .demo-account-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .brand-icon {
      width: 100px;
      height: 100px;
      margin: 0 0 var(--space-5);
      align-self: center;
    }

    ul {
      list-style: none;
      padding: 0;

      li {
        padding: var(--space-2) 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-2);
        opacity: 1;
        transition: all var(--transition-duration-default) var(--transition-animation-default);

        &.step--waiting {
          opacity: .3;
        }

        .step-message {
          font-weight: var(--font-weight-bold);
        }

        .step-description {
          margin-top: var(--space-1);
          font-size: var(--font-size-m);
        }
      }
    }

    .summary {
      margin-top: var(--space-4);

      a {
        text-decoration: none;
        color: var(--color-text-base);
      }
    }
  }
}
</style>
